import { Grid } from '../core/Grid.js';
import { GameType, GAME_TYPES, GAME_INITIALS } from '../persistence/types.js';
import { getGameTitle } from '../games/GameRegistry.js';
import { MetaTutorialId } from '../tutorials/TutorialEngine.js';
import {
    TutorialEntry,
    hasTutorial, hasVortexTutorial, getTutorial, getVortexTutorial,
    META_TUTORIAL_IDS, getMetaTutorial,
} from '../tutorials/TutorialRegistry.js';

const META_TUTORIAL_LABELS: Record<MetaTutorialId, { badge: string; title: string; badgeClass?: string; previewScene?: number }> = {
    basics: { badge: '?', title: 'Basics' },
    'vortex-hub': { badge: 'V', title: 'Vortex Hub', badgeClass: 'vortex', previewScene: 1 },
};

export interface TutorialHubCallbacks {
    onTutorialSelected: (gameType: GameType, isVortex: boolean) => void;
    onMetaTutorialSelected: (id: MetaTutorialId) => void;
    onWelcomeSelected: () => void;
    /** Open the Vortex welcome overview (card shown only on Vortex grids). */
    onVortexWelcomeSelected: () => void;
    onClose: () => void;
}

export class TutorialHub {
    private containerEl: HTMLElement;
    private callbacks: TutorialHubCallbacks;
    private currentGameType: GameType = 'mirrors';
    private isVortexGrid: boolean = false;

    constructor(containerEl: HTMLElement, callbacks: TutorialHubCallbacks) {
        this.containerEl = containerEl;
        this.callbacks = callbacks;
    }

    show(currentGameType: GameType, isVortexGrid: boolean): void {
        this.currentGameType = currentGameType;
        this.isVortexGrid = isVortexGrid;
        this.render();
        this.containerEl.classList.remove('hidden');
    }

    hide(): void {
        this.containerEl.classList.add('hidden');
    }

    private render(): void {
        this.containerEl.innerHTML = '';

        const optionsList = document.createElement('div');
        optionsList.className = 'grid-options-list tutorial-hub-list';

        // Welcome intro card (Vortex variant alongside it on Vortex grids), then
        // meta-tutorials, always at top
        optionsList.appendChild(this.renderWelcomeEntry());
        if (this.isVortexGrid) optionsList.appendChild(this.renderVortexWelcomeEntry());
        this.renderMetaEntries(optionsList);

        if (this.isVortexGrid) {
            this.renderVortexList(optionsList);
        } else {
            this.renderBaseList(optionsList);
        }

        this.containerEl.appendChild(optionsList);
    }

    private renderMetaEntries(list: HTMLElement): void {
        for (const id of META_TUTORIAL_IDS) {
            if (id === 'vortex-hub' && !this.isVortexGrid) continue;
            const entry = getMetaTutorial(id);
            if (!entry) continue;
            list.appendChild(this.renderMetaEntry(id, entry));
        }
    }

    private renderBaseList(list: HTMLElement): void {
        // Pinned: current game's tutorial
        if (hasTutorial(this.currentGameType)) {
            list.appendChild(this.renderEntry(this.currentGameType, false, true));
        }

        // Remaining games alphabetically (GAME_TYPES is already alphabetical)
        for (const gameType of GAME_TYPES) {
            if (gameType === this.currentGameType) continue;
            if (!hasTutorial(gameType)) continue;
            list.appendChild(this.renderEntry(gameType, false, false));
        }
    }

    private renderVortexList(list: HTMLElement): void {
        // Pinned: current game's Vortex tutorial
        if (hasVortexTutorial(this.currentGameType)) {
            list.appendChild(this.renderEntry(this.currentGameType, true, true));
        }

        // Remaining Vortex tutorials
        for (const gameType of GAME_TYPES) {
            if (gameType === this.currentGameType) continue;
            if (!hasVortexTutorial(gameType)) continue;
            list.appendChild(this.renderEntry(gameType, true, false));
        }
    }

    private renderWelcomeEntry(): HTMLElement {
        const option = document.createElement('div');
        option.className = 'tutorial-hub-card';

        const previewWrap = document.createElement('div');
        previewWrap.className = 'tutorial-hub-preview-wrap';
        const preview = document.createElement('canvas');
        preview.className = 'tutorial-hub-preview';
        preview.width = 160;
        preview.height = 160;
        const entry = getMetaTutorial('basics');
        if (entry) this.renderEntryPreview(preview, entry, 0);
        previewWrap.appendChild(preview);
        const startHere = document.createElement('span');
        startHere.className = 'tutorial-hub-starthere';
        startHere.textContent = 'Start here';
        previewWrap.appendChild(startHere);
        option.appendChild(previewWrap);

        const titleRow = document.createElement('div');
        titleRow.className = 'tutorial-hub-title';
        const badge = document.createElement('span');
        badge.className = 'tutorial-hub-badge';
        badge.textContent = '★';
        const name = document.createElement('span');
        name.className = 'tutorial-hub-name';
        name.textContent = 'Welcome';
        titleRow.append(badge, name);
        option.appendChild(titleRow);

        option.addEventListener('click', () => {
            this.callbacks.onWelcomeSelected();
            this.hide();
            this.callbacks.onClose();
        });

        return option;
    }

    /** The Vortex counterpart of the Welcome card, shown only on Vortex grids. */
    private renderVortexWelcomeEntry(): HTMLElement {
        const option = document.createElement('div');
        option.className = 'tutorial-hub-card vortex';

        const preview = document.createElement('canvas');
        preview.className = 'tutorial-hub-preview';
        preview.width = 160;
        preview.height = 160;
        const entry = getMetaTutorial('vortex-hub');
        if (entry) this.renderEntryPreview(preview, entry, META_TUTORIAL_LABELS['vortex-hub'].previewScene);
        option.appendChild(preview);

        const titleRow = document.createElement('div');
        titleRow.className = 'tutorial-hub-title';
        const badge = document.createElement('span');
        badge.className = 'tutorial-hub-badge vortex';
        badge.textContent = '★';
        const name = document.createElement('span');
        name.className = 'tutorial-hub-name';
        name.textContent = 'Vortex Welcome';
        titleRow.append(badge, name);
        option.appendChild(titleRow);

        option.addEventListener('click', () => {
            this.callbacks.onVortexWelcomeSelected();
            this.hide();
            this.callbacks.onClose();
        });

        return option;
    }

    private renderMetaEntry(id: MetaTutorialId, entry: TutorialEntry): HTMLElement {
        const labels = META_TUTORIAL_LABELS[id];
        const option = document.createElement('div');
        option.className = 'tutorial-hub-card';
        // Vortex-themed meta cards (e.g. Vortex Hub) get the purple hover/border too.
        if (labels.badgeClass === 'vortex') option.classList.add('vortex');

        // Preview canvas
        const preview = document.createElement('canvas');
        preview.className = 'tutorial-hub-preview';
        preview.width = 160;
        preview.height = 160;
        this.renderEntryPreview(preview, entry, labels.previewScene);
        option.appendChild(preview);

        // Title row with badge + name
        const titleRow = document.createElement('div');
        titleRow.className = 'tutorial-hub-title';

        const badge = document.createElement('span');
        badge.className = 'tutorial-hub-badge';
        badge.textContent = labels.badge;
        if (labels.badgeClass) {
            badge.classList.add(labels.badgeClass);
        }

        const name = document.createElement('span');
        name.className = 'tutorial-hub-name';
        name.textContent = labels.title;

        titleRow.appendChild(badge);
        titleRow.appendChild(name);
        option.appendChild(titleRow);

        // Click handler
        option.addEventListener('click', () => {
            this.callbacks.onMetaTutorialSelected(id);
            this.hide();
            this.callbacks.onClose();
        });

        return option;
    }

    private renderEntry(gameType: GameType, isVortex: boolean, pinned: boolean): HTMLElement {
        const option = document.createElement('div');
        option.className = 'tutorial-hub-card';
        if (pinned) {
            option.classList.add('pinned');
        }
        if (isVortex) {
            option.classList.add('vortex');
        }

        // Preview canvas
        const preview = document.createElement('canvas');
        preview.className = 'tutorial-hub-preview';
        preview.width = 160;
        preview.height = 160;
        const entry = isVortex ? getVortexTutorial(gameType) : getTutorial(gameType);
        if (entry) {
            this.renderEntryPreview(preview, entry);
        }
        option.appendChild(preview);

        // Title row with badge + name
        const titleRow = document.createElement('div');
        titleRow.className = 'tutorial-hub-title';

        const badge = document.createElement('span');
        badge.className = 'tutorial-hub-badge';
        badge.textContent = GAME_INITIALS[gameType];
        if (isVortex) {
            badge.classList.add('vortex');
        }

        const name = document.createElement('span');
        name.className = 'tutorial-hub-name';
        name.textContent = (isVortex ? 'Vortex ' : '') + getGameTitle(gameType);

        titleRow.appendChild(badge);
        titleRow.appendChild(name);
        option.appendChild(titleRow);

        // Click handler on whole card
        option.addEventListener('click', () => {
            this.callbacks.onTutorialSelected(gameType, isVortex);
            this.hide();
            this.callbacks.onClose();
        });

        return option;
    }

    private renderEntryPreview(canvas: HTMLCanvasElement, entry: TutorialEntry, sceneIndex?: number): void {
        const { definition, createAdapter } = entry;
        const grid = Grid.deserialize(definition.gridCode);
        const adapter = createAdapter(canvas, grid);

        const idx = sceneIndex ?? definition.previewScene ?? 0;
        const scene = definition.scenes[idx] ?? definition.scenes[0];
        adapter.applyState(scene.gameState);
        adapter.render(scene.renderOptions);
    }
}
